// companySlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	createCompany,
	updateCompany,
	fetchCompanyById,
} from "../thunks/companyThunks";
import { getFromStorage, saveToStorage } from "@utils/storage";

interface CompanyState {
	data: Record<string, any> | null;
	status: "idle" | "loading" | "succeeded" | "failed";
	error: string | null;
}

const initialState: CompanyState = {
	data: getFromStorage("company") || null,
	status: "idle",
	error: null,
};

const ensureArray = (v: any) => (Array.isArray(v) ? v : []);

const companySlice = createSlice({
	name: "company",
	initialState,
	reducers: {
		clearCompanyState: (state) => {
			state.data = null;
			state.status = "idle";
			state.error = null;
			saveToStorage("company", null);
		},

		// ✅ DRIVERS
		upsertCompanyDriver: (state, action: PayloadAction<any>) => {
			if (!state.data) return;

			const next = { ...state.data };
			const list = ensureArray(next.drivers);

			const incoming = action.payload;
			const id = Number(incoming?.id);
			if (!Number.isFinite(id)) return;

			const idx = list.findIndex((d: any) => Number(d?.id) === id);

			if (idx === -1) list.unshift(incoming);
			else list[idx] = { ...list[idx], ...incoming };

			next.drivers = list;
			state.data = next;
			saveToStorage("company", next);
		},

		removeCompanyDriver: (state, action: PayloadAction<number>) => {
			if (!state.data) return;

			const next = { ...state.data };
			const list = ensureArray(next.drivers);

			next.drivers = list.filter(
				(d: any) => Number(d?.id) !== Number(action.payload),
			);

			state.data = next;
			saveToStorage("company", next);
		},

		// ✅ VEHICLES (same pattern)
		upsertCompanyVehicle: (state, action: PayloadAction<any>) => {
			if (!state.data) return;

			const next = { ...state.data };
			const list = ensureArray(next.vehicles);

			const incoming = action.payload;
			const id = Number(incoming?.id);
			if (!Number.isFinite(id)) return;

			const idx = list.findIndex((v: any) => Number(v?.id) === id);

			if (idx === -1) list.unshift(incoming);
			else list[idx] = { ...list[idx], ...incoming };

			next.vehicles = list;
			state.data = next;
			saveToStorage("company", next);
		},

		removeCompanyVehicle: (state, action: PayloadAction<number>) => {
			if (!state.data) return;

			const next = { ...state.data };
			const list = ensureArray(next.vehicles);

			next.vehicles = list.filter(
				(v: any) => Number(v?.id) !== Number(action.payload),
			);

			state.data = next;
			saveToStorage("company", next);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(createCompany.pending, (state) => {
				state.status = "loading";
				state.error = null;
			})
			.addCase(createCompany.fulfilled, (state, action: PayloadAction<any>) => {
				state.status = "succeeded";
				state.data = action.payload;
				saveToStorage("company", action.payload);
			})
			.addCase(createCompany.rejected, (state, action) => {
				state.status = "failed";
				state.error = action.payload as string;
			});

		builder
			.addCase(updateCompany.pending, (state) => {
				state.status = "loading";
				state.error = null;
			})
			.addCase(updateCompany.fulfilled, (state, action: PayloadAction<any>) => {
				state.status = "succeeded";
				state.data = { ...state.data, ...action.payload };
				saveToStorage("company", state.data);
			})
			.addCase(updateCompany.rejected, (state, action) => {
				state.status = "failed";
				state.error = action.payload as string;
			});

		builder
			.addCase(fetchCompanyById.pending, (state) => {
				state.status = "loading";
				state.error = null;
			})
			.addCase(
				fetchCompanyById.fulfilled,
				(state, action: PayloadAction<any>) => {
					state.status = "succeeded";
					state.data = action.payload;
					saveToStorage("company", action.payload);
				},
			)
			.addCase(fetchCompanyById.rejected, (state, action) => {
				state.status = "failed";
				state.error = action.payload as string;
			});
	},
});

export const {
	clearCompanyState,
	upsertCompanyDriver,
	removeCompanyDriver,
	upsertCompanyVehicle,
	removeCompanyVehicle,
} = companySlice.actions;

export default companySlice.reducer;
